// Tiny shared helpers. We avoid pulling jQuery, classnames, lodash, or any
// other helper libraries — the entire JS layer should remain dependency-free.

export type Cleanup = () => void;

export const $ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document): T | null =>
  root.querySelector<T>(sel);

export const $$ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document): T[] =>
  Array.from(root.querySelectorAll<T>(sel));

export const on = <K extends keyof HTMLElementEventMap>(
  el: EventTarget,
  type: K,
  handler: (this: EventTarget, ev: HTMLElementEventMap[K]) => unknown,
  opts?: AddEventListenerOptions | boolean,
): Cleanup => {
  el.addEventListener(type, handler as EventListener, opts);
  return () => el.removeEventListener(type, handler as EventListener, opts);
};

export const ready = (cb: () => void): void => {
  if (document.readyState !== 'loading') queueMicrotask(cb);
  else document.addEventListener('DOMContentLoaded', cb, { once: true });
};

export const trapFocus = (root: HTMLElement): Cleanup => {
  const sel = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const handler = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const focusables = $$(sel, root).filter(el => !(el as HTMLElement).hidden);
    if (focusables.length === 0) return;
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    const active = document.activeElement;
    if (e.shiftKey && active === first) { (last as HTMLElement).focus(); e.preventDefault(); }
    else if (!e.shiftKey && active === last) { (first as HTMLElement).focus(); e.preventDefault(); }
  };
  return on(root, 'keydown', handler as never);
};

export const uid = (prefix = 'bf'): string =>
  `${prefix}-${Math.random().toString(36).slice(2, 9)}`;

export const clamp = (n: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, n));
